import type { IndexUiState, UiState } from "instantsearch.js/es/types";
import type { RouterProps } from "instantsearch.js/es/middlewares/createRouterMiddleware";
import { history } from "instantsearch.js/es/lib/routers";
import {
  localizedFacetSource,
  PromoAlgoliaCodeMapping,
  toLocalizedFacets,
  uiStateToSerializableParams
} from "components/Algolia/utils";
import { Language } from "src/utils/localization/languages/types";
import { CountryCode } from "@silversea-ssc/dotcom-read-model-types/Common";

const fixWrongSearchUrlParams = (
  searchState: IndexUiState,
  language: Language
): IndexUiState & { q: undefined } => {
  const refinementList = searchState.refinementList;
  if (refinementList) {
    let departureYearMonth = refinementList["departureYearMonth"];
    if (departureYearMonth) {
      // Some email campaigns have a wrong value for departureYearMonth: hash instead of array
      if (!Array.isArray(departureYearMonth))
        departureYearMonth = Object.values(departureYearMonth);

      refinementList["departureYearMonth"] = departureYearMonth;
    }

    // migrate from destinationName to destinationNames.en
    const destinationName = refinementList["destinationName"];
    if (destinationName) {
      delete refinementList["destinationName"];
      refinementList[localizedFacetSource(language, `destinationNames`)] =
        destinationName;
    }
  }

  let query = searchState.query;
  if (searchState["q"]) {
    const overrideQuery = searchState["q"];
    searchState["q"] = undefined;
    query = overrideQuery;
  }

  return {
    ...searchState,
    toggle: fixRouteAvailableToggle(searchState.toggle),
    refinementList,
    query,
    q: undefined
  };
};

const false_becauseTypeIsBooleanButActuallyString =
  "false" as unknown as boolean;

const fixRouteAvailableToggle = (
  toggle: IndexUiState["toggle"]
): IndexUiState["toggle"] => {
  if (toggle?.["available"] === undefined)
    return {
      ...(toggle ?? {}),
      available: true
    };
  if (toggle?.["available"] === false_becauseTypeIsBooleanButActuallyString) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { available, ...rest } = toggle;
    return rest;
  }

  return toggle;
};

const fixUiStateAvailableToggle = (
  toggle: IndexUiState["toggle"]
): IndexUiState["toggle"] => {
  if (toggle?.["available"] === undefined)
    return {
      ...(toggle ?? {}),
      available: false_becauseTypeIsBooleanButActuallyString
    };
  return toggle;
};

export const buildAlgoliaRouting = (
  localizedSearchIndexName: string,
  language: Language,
  countryCode: CountryCode | undefined,
  promoAlgoliaCodeMapping: PromoAlgoliaCodeMapping
) => {
  const algoliaRouting: RouterProps<UiState, IndexUiState> = {
    router: history({ cleanUrlOnDispose: false }),
    stateMapping: {
      stateToRoute(uiState) {
        const filteredSearchState = uiStateToSerializableParams(
          uiState,
          localizedSearchIndexName,
          promoAlgoliaCodeMapping
        );
        return {
          ...filteredSearchState,
          toggle: fixUiStateAvailableToggle(filteredSearchState.toggle)
        };
      },
      routeToState(routeState) {
        return {
          [localizedSearchIndexName]: fixWrongSearchUrlParams(
            {
              ...routeState,
              refinementList: toLocalizedFacets(
                language,
                countryCode,
                routeState.refinementList,
                promoAlgoliaCodeMapping
              )
            },
            language
          )
        };
      }
    }
  };

  return algoliaRouting;
};
